import { useMemo, useRef } from "react";
import { HotTable, type HotTableRef } from "@handsontable/react-wrapper";
import { registerAllModules } from "handsontable/registry";
import "handsontable/styles/handsontable.css";

registerAllModules();

export interface HandsontableGridColumn<T extends Record<string, string>> {
  key: keyof T & string;
  title: string;
  width?: number;
}

interface HandsontableGridProps<T extends Record<string, string>> {
  data: T[];
  columns: HandsontableGridColumn<T>[];
  onDataChange: (rows: T[]) => void;
  className?: string;
  tableHeight?: number | string;
}

export function HandsontableGrid<T extends Record<string, string>>({
  data,
  columns,
  onDataChange,
  className,
  tableHeight = 320,
}: HandsontableGridProps<T>) {
  const tableRef = useRef<HotTableRef | null>(null);

  const columnSettings = useMemo(
    () =>
      columns.map((column) => ({
        data: column.key,
        type: "text",
        width: column.width,
      })),
    [columns],
  );

  return (
    <div className={className}>
      <HotTable
        ref={tableRef}
        data={data}
        columns={columnSettings}
        colHeaders={columns.map((column) => column.title)}
        rowHeaders
        contextMenu
        manualColumnResize
        stretchH="all"
        height={tableHeight}
        licenseKey="non-commercial-and-evaluation"
        afterChange={(changes, source) => {
          if (!changes || source === "loadData") {
            return;
          }
          const nextRows = (tableRef.current?.hotInstance?.getSourceData() ??
            []) as T[];
          onDataChange(nextRows.map((row) => ({ ...row })));
        }}
      />
    </div>
  );
}
